import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import { ClockIcon, XIcon } from './Icons';

function formatTimeValue(hours: number, minutes: number): string {
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function parseTimeValue(raw: string): { hours: number; minutes: number } | null {
  const s = raw.trim();
  if (!s) return null;

  const match24 = s.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    const hours = parseInt(match24[1], 10);
    const minutes = parseInt(match24[2], 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return { hours, minutes };
    }
    return null;
  }

  const match12 = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match12) {
    let hours = parseInt(match12[1], 10);
    const minutes = parseInt(match12[2], 10);
    const period = match12[3].toUpperCase();
    if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) return null;
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return { hours, minutes };
  }

  const compact = s.match(/^(\d{2})(\d{2})$/);
  if (compact) {
    const hours = parseInt(compact[1], 10);
    const minutes = parseInt(compact[2], 10);
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return { hours, minutes };
    }
  }

  return null;
}

interface TimeInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange' | 'size'> {
  value?: string;
  onChange?: (event: { target: { name?: string; value: string; type: string } }) => void;
  placeholder?: string;
  showClearButton?: boolean;
  showQuickFill?: boolean;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'default';
}

const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  name,
  placeholder = 'Select time',
  required = false,
  readOnly = false,
  disabled = false,
  className,
  showClearButton = true,
  showQuickFill = true,
  error,
  helperText,
  size = 'default',
  ...props
}) => {
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [keyboardInputValue, setKeyboardInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownRect, setDropdownRect] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    placement: 'top' | 'bottom';
  } | null>(null);

  const parsedValue = value ? parseTimeValue(value) : null;
  const [hours, setHours] = useState(parsedValue?.hours ?? 0);
  const [minutes, setMinutes] = useState(parsedValue?.minutes ?? 0);
  const [timeSelectMode, setTimeSelectMode] = useState<'hour' | 'minute'>('hour');

  const hour12 = hours % 12 || 12;
  const isPM = hours >= 12;
  const minuteOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const emitValue = (h: number, m: number) => {
    onChange?.({ target: { name, value: formatTimeValue(h, m), type: 'time' } });
  };

  const handleTimeChange = (newHours: number, newMinutes: number) => {
    const h = Math.max(0, Math.min(23, newHours));
    const m = Math.max(0, Math.min(59, newMinutes));
    setHours(h);
    setMinutes(m);
    emitValue(h, m);
  };

  const setPM = () => {
    if (!isPM) handleTimeChange(hours + 12, minutes);
  };

  const setAM = () => {
    if (isPM) handleTimeChange(hours - 12, minutes);
  };

  const setHourFromClock = (n: number) => {
    const h = isPM ? (n === 12 ? 12 : n + 12) : n === 12 ? 0 : n;
    handleTimeChange(h, minutes);
    setTimeSelectMode('minute');
  };

  const setMinuteFromClock = (m: number) => {
    handleTimeChange(hours, m);
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setHours(0);
    setMinutes(0);
    setKeyboardInputValue('');
    onChange?.({ target: { name, value: '', type: 'time' } });
  };

  const commitKeyboardInput = () => {
    const parsed = parseTimeValue(keyboardInputValue);
    if (parsed) {
      setHours(parsed.hours);
      setMinutes(parsed.minutes);
      emitValue(parsed.hours, parsed.minutes);
    }
    setKeyboardInputValue('');
  };

  const quickFillNow = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || readOnly) return;
    const now = new Date();
    handleTimeChange(now.getHours(), now.getMinutes());
  };

  const quickFillMinutes = (m: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || readOnly) return;
    handleTimeChange(hours, m);
  };

  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) {
      setDropdownRect(null);
      return;
    }
    const updateRect = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
      const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
      const gap = 6;
      const horizontalPad = 8;
      const preferredHeight = 360;
      const width = Math.min(240, Math.max(rect.width, 220));
      let left = rect.left;
      if (left + width > vw - horizontalPad) {
        left = Math.max(horizontalPad, vw - horizontalPad - width);
      }
      if (left < horizontalPad) left = horizontalPad;

      const spaceBelow = Math.max(0, vh - rect.bottom - gap);
      const spaceAbove = Math.max(0, rect.top - gap);
      const openUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;

      if (openUp) {
        setDropdownRect({
          bottom: vh - rect.top + gap,
          left,
          width,
          placement: 'top',
        });
      } else {
        setDropdownRect({
          top: rect.bottom + gap,
          left,
          width,
          placement: 'bottom',
        });
      }
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inDropdown = dropdownRef.current?.contains(target);
      if (!inTrigger && !inDropdown) {
        setIsOpen(false);
        setKeyboardInputValue('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, []);

  useEffect(() => {
    if (isOpen) setTimeSelectMode('hour');
  }, [isOpen]);

  useEffect(() => {
    if (parsedValue) {
      setHours(parsedValue.hours);
      setMinutes(parsedValue.minutes);
    } else {
      setHours(0);
      setMinutes(0);
    }
  }, [value]);

  const hasValue = Boolean(parsedValue);
  const displayValue = hasValue ? formatTimeValue(hours, minutes) : '';
  const currentTimeDisplay = hasValue ? displayValue : formatTimeValue(hours, minutes);

  return (
    <div ref={containerRef} className="relative">
      <input type="hidden" name={name} value={value || ''} readOnly required={required} {...props} />

      <div className="relative">
        <div
          className={cn(
            'flex items-center w-full min-w-0 gap-1 rounded-lg transition-all duration-200 border',
            size === 'sm' ? 'min-h-8 px-2 py-1 text-xs' : 'min-h-10 px-2.5 py-2 text-sm',
            colors.surface,
            colors.border,
            isOpen && 'ring-2 shadow-sm',
            isOpen && colors.primaryBorder,
            !isOpen && !error && colors.interactiveHover,
            error && 'border-red-500',
            disabled && 'opacity-50 cursor-not-allowed',
            disabled && colors.surfaceHover,
            readOnly && 'cursor-default',
            readOnly && colors.surfaceHover,
            !disabled && !readOnly && 'cursor-pointer',
            className,
          )}
          onClick={() => !disabled && !readOnly && setIsOpen(!isOpen)}
          tabIndex={disabled || readOnly ? -1 : 0}
          role="button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          <ClockIcon
            className={cn(
              'shrink-0',
              size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5',
              hasValue ? colors.primaryFg : colors.textMuted,
              !hasValue && 'opacity-70',
            )}
          />
          <div className={cn('min-w-0 flex-1 overflow-hidden', !hasValue && 'opacity-70')}>
            {hasValue ? (
              <span className={cn('block truncate font-medium tabular-nums', size === 'sm' ? 'text-xs' : 'text-sm', colors.primaryFg)}>
                {displayValue}
              </span>
            ) : (
              <span className={cn('block truncate', size === 'sm' ? 'text-xs' : 'text-sm', colors.textMuted)}>{placeholder}</span>
            )}
          </div>
          {showClearButton && hasValue && !disabled && !readOnly && (
            <button
              type="button"
              onClick={handleClear}
              className={cn(
                'ml-auto shrink-0 rounded p-1 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors',
                colors.textMuted,
                colors.interactiveHover,
                colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
              )}
              title="Clear"
              tabIndex={-1}
            >
              <XIcon className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
            </button>
          )}
        </div>
      </div>

      {isOpen && !readOnly && !disabled && dropdownRect &&
        createPortal(
          <div
            ref={dropdownRef}
            data-time-input-dropdown=""
            className={cn('fixed overflow-hidden rounded-xl border shadow-lg', colors.surface, colors.border)}
            style={{
              ...zStyle(Z_INDEX.portaledDropdown),
              left: dropdownRect.left,
              width: dropdownRect.width,
              ...(dropdownRect.placement === 'top'
                ? { bottom: dropdownRect.bottom }
                : { top: dropdownRect.top }),
            }}
          >
            <div className={cn('border-b px-2.5 py-2', colors.border, colors.text, colors.surfaceHover)}>
              <div className="flex items-center justify-between gap-2">
                <span className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>Time</span>
                <div
                  className={cn(
                    'min-w-[4.5rem] rounded border px-2 py-1 text-center text-sm font-medium tabular-nums',
                    colors.border,
                    colors.surface,
                    colors.text,
                  )}
                >
                  {currentTimeDisplay}
                </div>
              </div>
            </div>

            <div className={cn('flex flex-col items-center px-2.5 py-2', colors.text)}>
              <div className="mb-1 flex gap-1">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTimeSelectMode('hour');
                  }}
                  className={cn(
                    'rounded px-2 py-1 text-xs font-semibold transition-all duration-150',
                    timeSelectMode === 'hour'
                      ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                      : cn(colors.surfaceHover, colors.textMuted, colors.interactiveHover),
                  )}
                >
                  Hour
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTimeSelectMode('minute');
                  }}
                  className={cn(
                    'rounded px-2 py-1 text-xs font-semibold transition-all duration-150',
                    timeSelectMode === 'minute'
                      ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                      : cn(colors.surfaceHover, colors.textMuted, colors.interactiveHover),
                  )}
                >
                  Minute
                </button>
              </div>

              <div className={cn('relative h-[160px] w-[160px] rounded-full border-2 shadow-inner', colors.surfaceHover, colors.border)}>
                {timeSelectMode === 'hour' ? (
                  <>
                    <div
                      className={cn('absolute left-1/2 top-1/2 w-0.5 origin-bottom', colors.primary)}
                      style={{
                        height: '44px',
                        transform: `translate(-50%, -100%) rotate(${hour12 * 30}deg)`,
                      }}
                    />
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => {
                      const angle = ((n - 3) * 30 * Math.PI) / 180;
                      const r = 51;
                      const cx = 80;
                      const cy = 80;
                      const x = cx + r * Math.cos(angle);
                      const y = cy + r * Math.sin(angle);
                      const selected = hour12 === n;
                      return (
                        <button
                          key={n}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setHourFromClock(n);
                          }}
                          className={cn(
                            'absolute flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                            selected ? cn(colors.primary, colors.primaryText) : cn(colors.interactiveHover, colors.text),
                          )}
                          style={{ left: x - 16, top: y - 16 }}
                          aria-label={`Hour ${n}`}
                        >
                          {n}
                        </button>
                      );
                    })}
                  </>
                ) : (
                  <>
                    <div
                      className={cn('absolute left-1/2 top-1/2 w-0.5 origin-bottom', colors.primary)}
                      style={{
                        height: '44px',
                        transform: `translate(-50%, -100%) rotate(${minutes * 6}deg)`,
                      }}
                    />
                    {minuteOptions.map((m) => {
                      const i = m / 5;
                      const angle = (((i === 0 ? 12 : i) - 3) * 30 * Math.PI) / 180;
                      const r = 51;
                      const cx = 80;
                      const cy = 80;
                      const x = cx + r * Math.cos(angle);
                      const y = cy + r * Math.sin(angle);
                      const selected = minutes === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMinuteFromClock(m);
                          }}
                          className={cn(
                            'absolute flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium tabular-nums transition-colors',
                            selected ? cn(colors.primary, colors.primaryText) : cn(colors.interactiveHover, colors.text),
                          )}
                          style={{ left: x - 16, top: y - 16 }}
                          aria-label={`Minute ${m}`}
                        >
                          {String(m).padStart(2, '0')}
                        </button>
                      );
                    })}
                  </>
                )}
              </div>

              <div className="mt-1 flex gap-1">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAM();
                  }}
                  className={cn(
                    'rounded px-3 py-1 text-xs font-semibold transition-all duration-150',
                    !isPM
                      ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                      : cn(colors.surfaceHover, colors.textMuted, colors.interactiveHover),
                  )}
                >
                  AM
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPM();
                  }}
                  className={cn(
                    'rounded px-3 py-1 text-xs font-semibold transition-all duration-150',
                    isPM
                      ? cn(colors.primary, colors.primaryText, 'shadow-sm')
                      : cn(colors.surfaceHover, colors.textMuted, colors.interactiveHover),
                  )}
                >
                  PM
                </button>
              </div>
            </div>

            <div className={cn('mx-2.5 mb-2.5 rounded-lg border px-2.5 py-2', colors.border, colors.surfaceHover)}>
              <div className={cn('mb-1.5 flex items-center gap-1.5', colors.text)}>
                <span className={cn('whitespace-nowrap text-xs font-medium', colors.textMuted)}>Or type</span>
                <input
                  type="text"
                  value={keyboardInputValue !== '' ? keyboardInputValue : currentTimeDisplay}
                  onChange={(e) => setKeyboardInputValue(e.target.value)}
                  onFocus={() => setKeyboardInputValue(currentTimeDisplay)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), commitKeyboardInput())}
                  onBlur={commitKeyboardInput}
                  placeholder="HH:MM"
                  className={cn(
                    'min-w-0 flex-1 rounded border px-2 py-1.5 text-sm outline-none transition-shadow focus:ring-2 focus:ring-offset-0',
                    colors.border,
                    colors.primaryBorderAll ? `focus:${colors.primaryBorderAll.replace('border-', 'ring-')}` : '',
                    colors.surface,
                    colors.text,
                  )}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {showQuickFill && (
                <div className="flex flex-wrap items-center gap-1">
                  <span className={cn('text-xs font-medium', colors.textMuted)}>Quick</span>
                  <button
                    type="button"
                    onClick={quickFillNow}
                    className={cn('rounded px-2 py-1 text-xs font-medium transition-colors', colors.info, colors.interactiveHover)}
                  >
                    Now
                  </button>
                  {[0, 15, 30, 45].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={quickFillMinutes(m)}
                      className={cn(
                        'rounded px-2 py-1 text-xs font-medium transition-colors',
                        colors.surfaceHover,
                        colors.textSecondary,
                        colors.interactiveHover,
                      )}
                    >
                      :{String(m).padStart(2, '0')}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}

      <div className="mt-1 flex flex-col gap-0.5">
        {error && <div className={cn('text-sm', colors.error)}>{error}</div>}
        {!error && helperText && <div className={cn('text-sm', colors.textMuted)}>{helperText}</div>}
      </div>
    </div>
  );
};

TimeInput.displayName = 'TimeInput';

export default TimeInput;
